import { Dialog } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { Dayjs } from 'dayjs'
import { CSSProperties, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import ActionContainer from 'components/containers/actionContainer'
import ModalLayout from 'components/layouts/modalLayout'
import PageTitleComponent from 'components/parts/pageTitle'
import { securityApi } from 'hooks/api/security'
import { shiftQuestionnaireApi } from 'hooks/api/shiftQuestionnaire'
import NonAnsweredListModal from 'pages/security/components/nonAnsweredListModal'
import SecurityRegisterModal from 'pages/security/components/securityRegisterModal'
import ShiftQuestionnaireModal from 'pages/security/components/shiftQuestionnaireModal'
import { addSnackbar } from 'store/snackbarSlice'
import { ApiSecurityFilterWithDetail, Security } from 'types/security'
import { Cls1SecurityDetail, Cls2SecurityDetail } from 'types/securityDetail'
import { Icons } from 'variables'

import SecurityTableComponent from './components/securityTable'
import FlatLayout from '../../components/layouts/flatLayout'

const apiCall = {
  get: async () => {
    const response = await securityApi().filterWithDetail()
    return response.data
  },

  add: async (data: {
    security: Omit<Security, 'id'>
    cls1Detail: Cls1SecurityDetail
    cls2Detail: Cls2SecurityDetail
  }) => {
    await securityApi().add(data)
  },

  update: async (data: {
    security: Security
    cls1Detail: Cls1SecurityDetail
    cls2Detail: Cls2SecurityDetail
  }) => {
    await securityApi().update(data)
  },

  delete: async (data: { securityIds: string[] }) => {
    await securityApi().bulkDelete(data)
  },

  sendEmail: async (
    securityIds: string[],
    year: number,
    month: number,
    expire: Dayjs,
  ) => {
    await shiftQuestionnaireApi().sendEmail(securityIds, year, month, expire)
  },

  sendRemindEmail: async (
    year: number,
    month: number,
    securityIds: string[],
  ) => {
    await shiftQuestionnaireApi().sendRemindEmail(year, month, securityIds)
  },
}

function useModalOpener<TProps>() {
  const [openModal, setOpenModal] = useState<TProps | boolean>(false)
  const open = () => {
    setOpenModal(true)
  }
  const openWithValue = (props: TProps) => {
    setOpenModal(props)
  }

  const close = () => setOpenModal(false)
  const getValue = (): TProps | undefined => {
    if (typeof openModal === 'boolean') {
      return undefined
    } else {
      return openModal
    }
  }
  const isOpen = () => {
    if (openModal === false) return false
    return openModal != null
  }
  return { isOpen, getValue, open, openWithValue, close }
}

const getSecurityRegisterDefaultValue = (
  security?: ApiSecurityFilterWithDetail,
) => {
  if (security != null) {
    const maxRepeatedly: number | '' = security.cls1Detail.maxRepeatedly ?? ''
    return {
      id: security.id,
      firstName: security.firstName,
      lastName: security.lastName,
      code: security.originalCode,
      email: security.cls2Detail.email,
      phone: security.cls2Detail.phoneNumber1,
      favoriteWork: new Set(security.cls1Detail.favoriteWork),
      maxRepeatedly: maxRepeatedly,
      fixShiftDays: security.cls1Detail.fixShiftDays,
    }
  }
}

export default function SecurityPage() {
  const dispatch = useDispatch()
  const [securities, setSecurities] = useState<ApiSecurityFilterWithDetail[]>(
    [],
  )

  const [checkedSecurityIds, setCheckedSecurityIds] = useState<string[]>([])
  const openQuestionnaireModal = useModalOpener()
  const openSecurityRegisterModal =
    useModalOpener<ApiSecurityFilterWithDetail>()
  const openNonAnsweredListModal = useModalOpener()

  const fetch = async () => {
    setSecurities(await apiCall.get())
  }

  useEffect(() => {
    fetch().then()
  }, [])

  // == 隊員編集系 ハンドラー ==
  const handleRemoveSecurities = async () => {
    if (
      // 誰も選択していない場合は何も起きない
      checkedSecurityIds.length === 0 ||
      // 突然消えると怖いので、確認ダイアログを打ち上げる
      !window.confirm(
        `${checkedSecurityIds.length}名削除されます。\nよろしいですか？`,
      )
    )
      return

    await apiCall.delete({ securityIds: checkedSecurityIds })
    await fetch()
    dispatch(addSnackbar({ message: '削除しました', severity: 'success' }))
    openSecurityRegisterModal.close()
  }

  const handleAddSecurity = async (data: {
    security: Omit<Security, 'id'>
    cls1Detail: Cls1SecurityDetail
    cls2Detail: Cls2SecurityDetail
  }) => {
    await apiCall.add(data)
    await fetch()
    openSecurityRegisterModal.close()
    dispatch(addSnackbar({ message: '登録しました', severity: 'success' }))
  }

  const handleUpdateSecurity = async (data: {
    security: Security
    cls1Detail: Cls1SecurityDetail
    cls2Detail: Cls2SecurityDetail
  }) => {
    await apiCall.update(data)
    await fetch()
    openSecurityRegisterModal.close()
    dispatch(addSnackbar({ message: '更新しました', severity: 'success' }))
  }

  // == シフト依頼系 ハンドラー ==
  const handleOpenQuestionnaireModal = () => {
    if (checkedSecurityIds.length === 0) {
      dispatch(
        addSnackbar({
          message: '隊員を選択してください',
          severity: 'error',
        }),
      )
      return
    } else {
      openQuestionnaireModal.open()
    }
  }

  const handleSendQuestionnaire = async (data: {
    year: number
    month: number
    expire: Dayjs
  }) => {
    await apiCall.sendEmail(
      checkedSecurityIds,
      data.year,
      data.month,
      data.expire,
    )
    dispatch(addSnackbar({ message: '送信しました', severity: 'success' }))
    openQuestionnaireModal.close()
  }

  // == 未提出者確認系 ハンドラー ==
  const handleReminderSend = async (data: {
    year: number
    month: number
    securityIds: string[]
  }) => {
    await apiCall.sendRemindEmail(data.year, data.month, data.securityIds)
    dispatch(addSnackbar({ message: '送信しました', severity: 'success' }))
    openNonAnsweredListModal.close()
  }

  return (
    <>
      <FlatLayout title="隊員" style={contentStyle}>
        <PageTitleComponent>隊員一覧</PageTitleComponent>

        {/*右上のボタン群*/}
        <Box style={pageActionContainerStyle}>
          <Button
            variant="outlined"
            onClick={handleOpenQuestionnaireModal}
            style={pageActionButtonStyle}
          >
            <Icons.sendMail style={pageActionIconStyle} />
            シフト提出一括指示
          </Button>
          <Button
            variant="outlined"
            onClick={openNonAnsweredListModal.open}
            style={pageActionButtonStyle}
          >
            <Icons.warning style={pageActionIconStyle} />
            シフト未提出者リスト
          </Button>
        </Box>

        <ActionContainer style={actionContainerStyle}>
          <Button onClick={openSecurityRegisterModal.open} variant="outlined">
            <Icons.addButton />
            追加
          </Button>
          <Button onClick={handleRemoveSecurities} variant="outlined">
            <Icons.deleteButton />
            削除
          </Button>
        </ActionContainer>

        <SecurityTableComponent
          securities={securities}
          onClickSecurity={openSecurityRegisterModal.openWithValue}
          onChangeChecked={setCheckedSecurityIds}
        />
      </FlatLayout>

      <Dialog open={openSecurityRegisterModal.isOpen()}>
        <ModalLayout title="隊員登録">
          <SecurityRegisterModal
            onCancel={openSecurityRegisterModal.close}
            onAdd={handleAddSecurity}
            onUpdate={handleUpdateSecurity}
            defaultValue={getSecurityRegisterDefaultValue(
              openSecurityRegisterModal.getValue(),
            )}
          />
        </ModalLayout>
      </Dialog>

      <Dialog open={openQuestionnaireModal.isOpen()}>
        <ModalLayout title="シフト提出一括指示">
          <ShiftQuestionnaireModal
            onCancel={openQuestionnaireModal.close}
            onSave={handleSendQuestionnaire}
          />
        </ModalLayout>
      </Dialog>

      <Dialog open={openNonAnsweredListModal.isOpen()} maxWidth="xl" fullWidth>
        {/*シフト未提出者リスト*/}
        <ModalLayout>
          <NonAnsweredListModal
            securities={securities}
            onCancel={openNonAnsweredListModal.close}
            onSend={handleReminderSend}
          />
        </ModalLayout>
      </Dialog>
    </>
  )
}

const contentStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
}

const pageActionContainerStyle: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 0,
  display: 'flex',
  gap: '8px',
  padding: '16px',
}

const actionContainerStyle: CSSProperties = {
  marginBottom: '8px',
}

const pageActionButtonStyle: CSSProperties = {
  padding: '4px 8px',
}

const pageActionIconStyle: CSSProperties = {
  marginRight: '4px',
}

// const securitySearchStyle: CSSProperties = {
//   width: '160px',
// }
